import React, {useEffect, useRef, useState} from "react";
import {Link as RouterLink, useNavigate, useLocation} from 'react-router-dom';
import {
    Alert,
    Button,
    CardContent,
    Checkbox,
    FormControlLabel,
    TextField,
} from "@mui/material";

import FormFieldLayout from "../components/layout/FormFieldLayout";
import FormControlsLayout from "../components/layout/FormControlsLayout";
import PageLayout from "../components/layout/PageLayout";
import {signIn, getRememberMeFromCookies} from "../store/securitySlice";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";


const LoginPage = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;
    const [oauthClients, setOauthClients] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [loginError, setLoginError] = useState(null);
    const [emailError, setEmailError] = useState(null);
    const [passwordError, setPasswordError] = useState(null);
    const autoLoginAttempted = useRef(false);

    useEffect(() => {
        fetchOauthClients();
    }, []);

    useEffect(() => {
        if (autoLoginAttempted.current) return;
        const remembered = getRememberMeFromCookies();
        if (remembered) {
            autoLoginAttempted.current = true;
            setEmail(remembered.email);
            dispatch(signIn(remembered.email, '', false, onLoginSuccess, onLoginError, { token: remembered.token, series: remembered.series }));
        }
    }, []);

    const fetchOauthClients = () => {
        fetch('/api/security/oauth/clients', {
            headers: {
                "Content-Type": "application/json"
            },
            method: 'GET',
            // body: JSON.stringify(credentials)
        })
            .then((response) => {
                if (response.ok) {
                    return response.json();
                }
                return Promise.reject(response);
            })
            .then((response) => {
                setOauthClients(response);
            })
            .catch(error => {
                onLoginError();
                console.error('There was an error!', error);
            });
    }

    const onLoginSuccess = () => {
        setLoginError(null);
        setEmailError(null);
        setPasswordError(null);
        navigate("/");
    };

    const onLoginError = (message) => {
        setEmailError(null);
        setPasswordError(null);
        setLoginError(message);
    };

    const handleSignIn = () => {
        setLoginError(null);
        setEmailError(null);
        setPasswordError(null);
        let invalid = false;
        if (!email.trim()) {
            setEmailError(t("auth.login.errors.emailRequired"));
            invalid = true;
        }
        if (!password.trim()) {
            setPasswordError(t("auth.login.errors.passwordRequired"));
            invalid = true;
        }
        if (invalid) {
            return;
        }
        dispatch(signIn(email.trim(), password, rememberMe, onLoginSuccess, onLoginError));
    };

    return (
        <PageLayout
            title={t("auth.login.title")}
            showMenuButton={false}
            showNavigationMenu={false}
            cardStyle={{marginLeft: 330, marginTop: 10, marginRight: 10, marginBottom: 10}}
        >
            <CardContent>
                    {successMessage && (
                        <FormFieldLayout>
                            <Alert severity="success" onClose={() => navigate('/signin', { replace: true, state: {} })}>
                                {successMessage}
                            </Alert>
                        </FormFieldLayout>
                    )}
                    {loginError && (
                        <FormFieldLayout>
                            <Alert severity="error" onClose={() => setLoginError(null)}>
                                {loginError}
                            </Alert>
                        </FormFieldLayout>
                    )}
                    <FormFieldLayout>
                        <TextField
                            required
                            label={t("auth.login.email")}
                            variant="standard"
                            sx={{width: 300}}
                            type="email"
                            value={email}
                            error={Boolean(emailError)}
                            helperText={emailError || undefined}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setLoginError(null);
                                setEmailError(null);
                            }}
                        />
                    </FormFieldLayout>
                    <FormFieldLayout>
                        <TextField
                            required
                            label={t("auth.login.password")}
                            variant="standard"
                            sx={{width: 300}}
                            type="password"
                            value={password}
                            error={Boolean(passwordError)}
                            helperText={passwordError || undefined}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setLoginError(null);
                                setPasswordError(null);
                            }}
                        />
                    </FormFieldLayout>
                    <FormFieldLayout>
                        <FormControlLabel control={<Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}/>} label={t("auth.login.rememberPassword")}/>
                    </FormFieldLayout>


                    {oauthClients &&
                        <FormFieldLayout>
                            {oauthClients.map(client =>
                                <FormFieldLayout key={client.name}><a href={client.url}>{client.name}</a></FormFieldLayout>)}
                        </FormFieldLayout>
                    }


                    <FormControlsLayout>
                        <Button variant="contained" onClick={handleSignIn}>{t("auth.login.signIn")}</Button>
                        <Button variant="outlined" component={RouterLink} to="/registration">{t("auth.login.signUp")}</Button>
                        <Button variant="outlined" component={RouterLink} to="/recovery">{t("auth.login.passwordRecovery")}</Button>
                    </FormControlsLayout>
            </CardContent>
        </PageLayout>
    );
}

export default LoginPage